/* ============================================================
   FGO HEADER — global behaviour
   ------------------------------------------------------------
   Split out of pdp-v2.js so the docking header works on every
   template. It used to bail immediately unless <body> carried
   the `pdp-v2` class, which only the product template sets.

   Owns three things: the measured promo-bar offset, the dock
   hysteresis, and the logo colour swap. Paired with
   fgo-header.css.
   ============================================================ */
/* ============================================================
   HEADER dock — hysteresis + measured promo height
   ------------------------------------------------------------
   The theme toggles .bg at a single threshold, so the header
   flickers when a scroll settles exactly on the boundary. The
   demo docks at one value and releases at a lower one, which
   cannot oscillate.

   Also measures the promo bar so the docked header sits under it
   rather than at a hardcoded offset.
   ============================================================ */
(function () {
  'use strict';

  var head = document.querySelector('#header');
  var promo = document.querySelector('[id*="theme-announcement"]');
  if (!head) return;

  /* --promo-h drives where the header sticks: top = --promo-h + 12 at rest,
     top = --promo-h when docked. Getting it wrong by writing 0 parks the
     header at the very top of the viewport, UNDER a promo bar that is
     z-index 101 — which is the bar sitting on top of the mark.

     This used to measure once, synchronously, at script execution, and then
     only again on resize. Run before the announcement bar has laid out,
     getBoundingClientRect().height returns 0, and 0 is what got written. The
     `var(--promo-h, 40px)` fallback in the CSS cannot save that: the property
     IS set, just set to zero, so the fallback never applies. The header then
     stayed under the bar for the life of the page unless you happened to
     resize the window, which is why it looked fine sometimes.

     Two guards. Never write a 0 while the element exists — remove the
     property instead, so the CSS fallback takes over. And keep measuring:
     on load, on font swap, and via ResizeObserver, since the bar is a
     rotating-message widget whose height can change after first paint. */
  var setPromoH = function () {
    if (!promo) {
      document.documentElement.style.setProperty('--promo-h', '0px');
      return;
    }
    var h = Math.round(promo.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty('--promo-h', h + 'px');
    else       document.documentElement.style.removeProperty('--promo-h');
  };
  setPromoH();
  addEventListener('resize', setPromoH, { passive: true });
  addEventListener('load', setPromoH);
  if (document.readyState === 'loading') {
    addEventListener('DOMContentLoaded', setPromoH);
  }
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(setPromoH).catch(function () {});
  }
  if (promo && window.ResizeObserver) {
    new ResizeObserver(setPromoH).observe(promo);
  }

  /* The theme's own handler (theme-header.js) decides docking from
     header.getBoundingClientRect().top <= 13. Our promo-bar offset
     (top: var(--promo-h)) makes that permanently false, so it never added
     .bg — and it strips the class on every scroll event, which defeated the
     cached `docked` flag this used to keep.

     It also drives the logo colour swap through .active on #headerLogo /
     #headerLogoAlt, so we cannot simply ignore it. Instead we state the full
     result unconditionally on every event, and register last (inside `load`,
     with a fresh function identity so it is a distinct listener) — listeners
     fire in registration order, so ours settles the class after the theme's. */

  var dark  = document.querySelector('#headerLogo');
  var light = document.querySelector('#headerLogoAlt');

  /* Two fixes for the flashing, and they are different problems.

     1. THE FLASH. The theme's handler strips .bg on every scroll event and we
        re-add it. Because the header transitions `background` over .25s, each
        strip/re-add restarted a cross-fade — which is the black-to-cream
        oscillation. Reading state off .bg made us complicit: the class we were
        reading was being cleared underneath us every frame.

        So our CSS now keys on `fgo-docked`, a class the theme has never heard
        of and therefore cannot remove. We still set .bg so the theme's own
        rules and anything else depending on it keep working, but nothing we
        style is driven by it. Our visual state changes only when the scroll
        state actually changes.

     2. THE BAND. 18/6 gave twelve pixels of hysteresis. One wheel notch is
        50-120px and momentum scrolling oscillates around a boundary, so the
        band was easy to straddle. 90/40 cannot be crossed by accident. */
  var DOCK_AT = 90, RELEASE_AT = 40;
  var docked = null;                 /* null = not yet applied */
  var ticking = false;

  var apply = function () {
    var y = window.scrollY;
    var next = docked ? y >= RELEASE_AT : y > DOCK_AT;

    /* .bg is kept in sync for the theme's benefit, cheaply and every time */
    if (head.classList.contains('bg') !== next) head.classList.toggle('bg', next);

    /* .active must ALSO be restated every event, not just on a state change.
       It is shared with the theme's handler, which rewrites it on every scroll
       event. Restating it only on transitions meant the theme won every event
       after the first — leaving the dark mark active on the black docked bar,
       i.e. an invisible logo. Same shared-ownership trap as .bg; toggle with
       an explicit force value is idempotent, so doing it every time is free. */
    if (dark  && dark.classList.contains('active')  === next)  dark.classList.toggle('active', !next);
    if (light && light.classList.contains('active') !== next) light.classList.toggle('active', next);

    if (next === docked) return;     /* geometry already correct */
    docked = next;
    head.classList.toggle('fgo-docked', next);
    /* Same state on <html>, because the element that actually carries the
       sticky offset is the Shopify section wrapper ABOVE #header, and a
       parent is not selectable from a child in CSS. See fgo-header.css. */
    document.documentElement.classList.toggle('fgo-head-docked', next);
  };

  /* rAF-throttled: scroll fires far faster than we can paint, and doing class
     work per event is what let the theme and this handler interleave mid-frame.

     A timer races the rAF because rAF starves in occluded windows, low-power
     mode and embedded panes — and a starved rAF here wedges `ticking` true
     forever, which kills docking for the life of the page. Whichever fires
     first wins; the loser no-ops. */
  var onScroll = function () {
    if (ticking) return;
    ticking = true;
    var done = false;
    var run = function () { if (done) return; done = true; ticking = false; apply(); };
    requestAnimationFrame(run);
    setTimeout(run, 120);
  };

  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('load', function () {
    addEventListener('scroll', function () { onScroll(); }, { passive: true });
    apply();
  });
  apply();

  /* The top transition is gated behind this class (see fgo-header.css):
     first paint and the first measured correction must SNAP, not slide —
     a page should not open with its header animating into place. Double
     rAF = one fully painted frame with settled values; the timer covers
     contexts where rAF starves. Whichever fires first wins. */
  var readyOnce = false;
  var armReady = function () {
    if (readyOnce) return;
    readyOnce = true;
    document.documentElement.classList.add('fgo-head-ready');
  };
  requestAnimationFrame(function () { requestAnimationFrame(armReady); });
  setTimeout(armReady, 300);
})();
